import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
    Box, Typography, Chip, Button, Skeleton, Avatar,
    Dialog, DialogTitle, DialogContent, DialogActions,
    CircularProgress, Tooltip, Autocomplete, TextField,
} from '@mui/material';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import Cookies from 'universal-cookie';

import {
    fetchWaitingQueueFromAPI,
    claimQueueEntryInAPI,
    claimNextInAPI,
    cancelQueueEntryInAPI,
    assignEntryToAgentInAPI,
    fetchAgentsByBusinessLine,
} from '../../Redux/WaitingQueueDucks';
import { fetchBusinessesFromAPI } from '../../Redux/BusinessDucks';

// Roles con permiso para derivar entradas de la cola.
const SUPERVISOR_ROLES = ['Super-Admin', 'Supervisor Ventas'];

// ── Helpers ────────────────────────────────────────────────────────────────────

const getPerfil = () => {
    const cookies = document.cookie.split('; ');
    const perfilCookie = cookies.find(c => c.startsWith('perfil='));
    return perfilCookie ? decodeURIComponent(perfilCookie.split('=')[1]) : '';
};

const isSupervisor = () => SUPERVISOR_ROLES.includes(getPerfil());

const getUserBusinessLineIds = () => {
    const cookies = document.cookie.split('; ');
    const perfilCookie = cookies.find(c => c.startsWith('perfil='));
    const perfil = perfilCookie ? decodeURIComponent(perfilCookie.split('=')[1]) : '';
    if (perfil === 'Super-Admin') return null;
    const blCookie = cookies.find(c => c.startsWith('businessLineIds='));
    if (!blCookie) return [];
    return blCookie.split('=')[1].split(',').map(Number).filter(Boolean);
};

const getCurrentUserId = () => {
    const token = new Cookies().get('token');
    if (!token) return null;
    try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const id = payload?.nameid ?? payload?.sub ?? payload?.userId ?? null;
        return id !== null ? parseInt(id, 10) : null;
    } catch {
        return null;
    }
};

const fmtPhone = (tel) => {
    if (!tel) return '';
    const s = String(tel);
    return s.length > 4 ? `+(${s.substring(0, 2)}) ${s.substring(2)}` : s;
};

const fmtWaitingTime = (enqueuedAt) => {
    if (!enqueuedAt) return '—';
    const diffMs = Date.now() - new Date(enqueuedAt).getTime();
    const totalMin = Math.floor(diffMs / 60000);
    if (totalMin < 1) return 'Ahora';
    if (totalMin < 60) return `${totalMin} min`;
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
};

const getInitials = (name) => {
    if (!name?.trim()) return '?';
    return name.trim().split(' ').slice(0, 2).map(w => w[0]?.toUpperCase()).join('');
};

const truncateText = (text, max = 90) => {
    const normalized = String(text || '').replace(/\s+/g, ' ').trim();
    if (!normalized) return '';
    return normalized.length > max ? `${normalized.slice(0, max - 1)}…` : normalized;
};

const getQueueSnippet = (entry) => truncateText(
    entry?.firstMessage ||
    entry?.firstClientMessage ||
    entry?.messagePreview ||
    entry?.lastMessage ||
    entry?.notes ||
    '',
    100
);

// ── QueueCard ──────────────────────────────────────────────────────────────────

const QueueCard = ({ entry, currentUserId, onClaim, onCancel, onAssign, canAssign, claiming }) => {
    const isReservedForMe = entry.isReserved && entry.reservedForUserId === currentUserId;
    const initials = getInitials(entry.clientName);
    const snippet = getQueueSnippet(entry);

    return (
        <Box sx={{
            width: '100%',
            maxWidth: '100%',
            boxSizing: 'border-box',
            mx: 0,
            my: 0.75,
            bgcolor: '#fff',
            border: isReservedForMe ? '1.5px solid #f97316' : '1px solid #e8e8e8',
            borderRadius: 2,
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 1.25,
            boxShadow: isReservedForMe
                ? '0 2px 12px rgba(249,115,22,0.12)'
                : '0 1px 4px rgba(0,0,0,0.05)',
            '&:hover .queue-actions': {
                opacity: 1,
                maxHeight: 56,
                mt: 0.5,
            },
        }}>
            {/* Fila superior */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Avatar sx={{
                    bgcolor: isReservedForMe ? '#f97316' : '#007AFF',
                    width: 40, height: 40, fontSize: 14, flexShrink: 0,
                }}>
                    {initials}
                </Avatar>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                        <Typography variant="body2" fontWeight={700} noWrap>
                            {entry.clientName || 'Sin nombre'}
                        </Typography>
                        {isReservedForMe && (
                            <Chip
                                label="Reservado para ti"
                                size="small"
                                sx={{
                                    bgcolor: '#fff7ed',
                                    color: '#c2410c',
                                    fontWeight: 700,
                                    fontSize: 10,
                                    height: 18,
                                    border: '1px solid #fed7aa',
                                }}
                            />
                        )}
                        {entry.isReserved && !isReservedForMe && entry.reservedForAgentName && (
                            <Chip
                                label={`Reservado: ${entry.reservedForAgentName}`}
                                size="small"
                                sx={{
                                    bgcolor: '#fef9c3',
                                    color: '#854d0e',
                                    fontWeight: 600,
                                    fontSize: 10,
                                    height: 18,
                                }}
                            />
                        )}
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                        {fmtPhone(entry.clientPhone)}
                    </Typography>
                </Box>

                <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                    <Typography variant="caption" fontWeight={700}
                        sx={{ color: '#6b7280', display: 'block' }}>
                        Pos. {entry.position}
                    </Typography>
                </Box>
            </Box>

            {/* Meta */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                    <AccessTimeIcon sx={{ fontSize: 13, color: '#9ca3af' }} />
                    <Typography variant="caption" color="text.secondary">
                        {`Esperando ${fmtWaitingTime(entry.enqueuedAt)}`}
                    </Typography>
                </Box>
                {entry.businessLineName && (
                    <Chip
                        label={entry.businessLineName}
                        size="small"
                        sx={{
                            height: 20,
                            fontSize: 10,
                            fontWeight: 600,
                            bgcolor: '#e8f0fe',
                            color: '#1a56db',
                        }}
                    />
                )}
                <Typography variant="caption" color="text.secondary">·</Typography>
                <Typography variant="caption" color="text.secondary">
                    Entró por: {entry.enqueuedBy || '—'}
                </Typography>
            </Box>
            {snippet && (
                <Typography variant="caption" color="text.secondary" noWrap>
                    {snippet}
                </Typography>
            )}

            {/* Acciones */}
            <Box
                className="queue-actions"
                sx={{
                    display: 'flex',
                    gap: 1,
                    overflow: 'hidden',
                    opacity: 0,
                    maxHeight: 0,
                    mt: 0,
                    transition: 'opacity 0.2s ease, max-height 0.2s ease, margin-top 0.2s ease',
                    '@media (hover: none)': {
                        opacity: 1,
                        maxHeight: 56,
                        mt: 0.5,
                    },
                }}
            >
                <Button
                    size="small"
                    variant="contained"
                    startIcon={claiming ? <CircularProgress size={12} sx={{ color: '#fff' }} /> : <PlayArrowIcon sx={{ fontSize: 14 }} />}
                    onClick={() => onClaim(entry)}
                    disabled={claiming}
                    sx={{
                        textTransform: 'none', fontWeight: 600, fontSize: 12,
                        borderRadius: 2, px: 1.75, py: 0.5, minHeight: 44,
                        bgcolor: isReservedForMe ? '#f97316' : '#007AFF',
                        '&:hover': { bgcolor: isReservedForMe ? '#ea580c' : '#005ecb' },
                    }}
                >
                    {claiming ? 'Tomando...' : 'Tomar'}
                </Button>

                {canAssign && (
                    <Tooltip title="Derivar a un consultor">
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<AssignmentIndIcon sx={{ fontSize: 14 }} />}
                            onClick={() => onAssign(entry)}
                            sx={{
                                textTransform: 'none', fontWeight: 600, fontSize: 12,
                                borderRadius: 2, px: 1.5, py: 0.5, minHeight: 44,
                                borderColor: '#7c3aed', color: '#7c3aed',
                                '&:hover': { borderColor: '#6d28d9', bgcolor: '#f5f3ff' },
                            }}
                        >
                            Derivar
                        </Button>
                    </Tooltip>
                )}

                <Tooltip title="Cancelar y devolver al bot">
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<CancelOutlinedIcon sx={{ fontSize: 14 }} />}
                        onClick={() => onCancel(entry)}
                        sx={{
                            textTransform: 'none', fontWeight: 600, fontSize: 12,
                            borderRadius: 2, px: 1.5, py: 0.5, minHeight: 44,
                            borderColor: '#e0e0e0', color: '#757575',
                            '&:hover': { borderColor: '#d32f2f', color: '#d32f2f', bgcolor: '#fff5f5' },
                        }}
                    >
                        Cancelar
                    </Button>
                </Tooltip>
            </Box>
        </Box>
    );
};

// ── EmptyState ─────────────────────────────────────────────────────────────────

const EmptyQueue = () => (
    <Box sx={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', height: '60%', gap: 2, color: 'text.secondary',
    }}>
        <HourglassEmptyIcon sx={{ fontSize: 56, opacity: 0.25 }} />
        <Typography variant="body1" fontWeight={600}>No hay conversaciones en cola</Typography>
        <Typography variant="body2" color="text.secondary">
            Las conversaciones en espera aparecerán aquí.
        </Typography>
    </Box>
);

// ── Main page ──────────────────────────────────────────────────────────────────

const ColaEsperaPage = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const userBLIds = getUserBusinessLineIds();
    const currentUserId = getCurrentUserId();
    const defaultBL = (userBLIds !== null && userBLIds.length > 0) ? userBLIds[0] : null;

    const supervisor = isSupervisor();

    const [selectedBusinessLine, setSelectedBusinessLine] = useState(defaultBL);
    const [cancelTarget, setCancelTarget] = useState(null);
    const [cancelling, setCancelling] = useState(false);
    const [claimingId, setClaimingId] = useState(null);
    const [claimingNext, setClaimingNext] = useState(false);
    const [assignTarget, setAssignTarget] = useState(null);
    const [agents, setAgents] = useState([]);
    const [selectedAgentId, setSelectedAgentId] = useState(null);
    const [loadingAgents, setLoadingAgents] = useState(false);
    const [assigning, setAssigning] = useState(false);

    const { queueList, waitingCount, reservedCount, loading } =
        useSelector(state => state.waitingQueue);
    const allBusinessLines = useSelector(store => store.business);

    const businessLines = userBLIds === null
        ? (Array.isArray(allBusinessLines) ? allBusinessLines : [])
        : (Array.isArray(allBusinessLines) ? allBusinessLines.filter(bl => userBLIds.includes(bl.id)) : []);
    const businessLineOptions = [
        ...(userBLIds === null ? [{ id: null, label: 'Todas' }] : []),
        ...businessLines.map(bl => ({
            id: bl.id,
            label: bl.name || bl.nombre || bl.siglas || `BL ${bl.id}`,
        })),
    ];
    const selectedBusinessLineOption =
        businessLineOptions.find(option => option.id === selectedBusinessLine) || null;

    const loadQueue = useCallback(() => {
        dispatch(fetchWaitingQueueFromAPI(selectedBusinessLine));
    }, [dispatch, selectedBusinessLine]);

    useEffect(() => {
        dispatch(fetchBusinessesFromAPI());
    }, [dispatch]);

    useEffect(() => {
        loadQueue();
    }, [loadQueue]);

    useEffect(() => {
        const id = setInterval(loadQueue, 30_000);
        return () => clearInterval(id);
    }, [loadQueue]);

    const handleClaim = async (entry) => {
        setClaimingId(entry.id);
        try {
            const result = await dispatch(claimQueueEntryInAPI(entry.id));
            navigate('/smart_talking/conversaciones', {
                state: { conversationId: result?.conversationId ?? entry.conversationId },
            });
        } catch {
            // error ya logueado
        } finally {
            setClaimingId(null);
        }
    };

    const handleClaimNext = async () => {
        if (selectedBusinessLine === null) return;
        setClaimingNext(true);
        try {
            const result = await dispatch(claimNextInAPI(selectedBusinessLine));
            if (result) {
                navigate('/smart_talking/conversaciones', {
                    state: { conversationId: result.conversationId },
                });
            } else {
                loadQueue();
            }
        } catch {
            // error ya logueado
        } finally {
            setClaimingNext(false);
        }
    };

    const handleOpenAssign = async (entry) => {
        setAssignTarget(entry);
        setSelectedAgentId(null);
        setAgents([]);
        if (!entry.businessLineId) return;
        setLoadingAgents(true);
        try {
            const list = await fetchAgentsByBusinessLine(entry.businessLineId);
            setAgents(list);
        } catch {
            // error ya logueado
        } finally {
            setLoadingAgents(false);
        }
    };

    const handleAssignConfirm = async () => {
        if (!assignTarget || !selectedAgentId) return;
        setAssigning(true);
        try {
            await dispatch(assignEntryToAgentInAPI(assignTarget.id, selectedAgentId));
            setAssignTarget(null);
            setSelectedAgentId(null);
            loadQueue();
        } catch {
            // error ya logueado
        } finally {
            setAssigning(false);
        }
    };

    const handleCancelConfirm = async () => {
        if (!cancelTarget) return;
        setCancelling(true);
        try {
            await dispatch(cancelQueueEntryInAPI(cancelTarget.id));
            setCancelTarget(null);
            loadQueue();
        } catch {
            // error ya logueado
        } finally {
            setCancelling(false);
        }
    };

    if (userBLIds !== null && userBLIds.length === 0) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', flexDirection: 'column', gap: 2, color: 'text.secondary' }}>
                <Typography variant="h4">🏢</Typography>
                <Typography variant="body1" fontWeight={600}>Sin líneas de negocio asignadas</Typography>
                <Typography variant="body2">Contacta al administrador para que te asigne líneas de negocio.</Typography>
            </Box>
        );
    }

    const canClaimNext = selectedBusinessLine !== null && queueList.length > 0;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', bgcolor: '#fff' }}>

            {/* ── Header ── */}
            <Box sx={{
                px: 2.5, py: 1.5,
                borderBottom: '1px solid #e8e8e8',
                bgcolor: '#fff',
                flexShrink: 0,
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.25 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <HourglassEmptyIcon sx={{ color: '#007AFF', fontSize: 22 }} />
                        <Typography variant="h6" fontWeight={700}>Cola de espera</Typography>
                    </Box>

                    <Tooltip title={!canClaimNext ? 'Selecciona una línea de negocio' : 'Tomar la siguiente conversación disponible'}>
                        <span>
                            <Button
                                variant="contained"
                                size="small"
                                startIcon={
                                    claimingNext
                                        ? <CircularProgress size={13} sx={{ color: '#fff' }} />
                                        : <PlayArrowIcon sx={{ fontSize: 16 }} />
                                }
                                onClick={handleClaimNext}
                                disabled={!canClaimNext || claimingNext}
                                sx={{
                                    textTransform: 'none', fontWeight: 700, fontSize: 13,
                                    borderRadius: 2, px: 2, height: 32,
                                    bgcolor: '#007AFF', '&:hover': { bgcolor: '#005ecb' },
                                }}
                            >
                                {claimingNext ? 'Tomando...' : 'Tomar siguiente'}
                            </Button>
                        </span>
                    </Tooltip>
                </Box>

                {/* BL combobox */}
                <Autocomplete
                    size="small"
                    disablePortal
                    disableClearable
                    options={businessLineOptions}
                    value={selectedBusinessLineOption}
                    getOptionLabel={(option) => option?.label || ''}
                    isOptionEqualToValue={(option, value) => option.id === value?.id}
                    onChange={(_, option) => setSelectedBusinessLine(option?.id ?? null)}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Línea de negocio"
                            placeholder="Seleccionar..."
                        />
                    )}
                    sx={{
                        '& .MuiInputBase-root': { fontSize: 13 },
                        '& .MuiInputLabel-root': { fontSize: 13 },
                    }}
                />
            </Box>

            {/* ── Resumen ── */}
            {(waitingCount > 0 || reservedCount > 0) && (
                <Box sx={{
                    px: 2.5, py: 1,
                    bgcolor: '#f8faff',
                    borderBottom: '1px solid #e8e8e8',
                    display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0,
                }}>
                    <Typography variant="caption" fontWeight={600} color="text.secondary">
                        {waitingCount} esperando
                    </Typography>
                    {reservedCount > 0 && (
                        <>
                            <Typography variant="caption" color="text.secondary">·</Typography>
                            <Typography variant="caption" fontWeight={600} sx={{ color: '#f97316' }}>
                                {reservedCount} reservados para ti
                            </Typography>
                        </>
                    )}
                </Box>
            )}

            {/* ── Lista ── */}
            <Box sx={{
                flex: 1,
                overflowY: 'auto',
                overflowX: 'hidden',
                px: 2,
                py: 1.25,
                display: 'flex',
                flexDirection: 'column',
                bgcolor: '#f8fafc',
                boxSizing: 'border-box',
            }}>
                {loading && queueList.length === 0 ? (
                    Array.from({ length: 4 }).map((_, i) => (
                        <Box key={i} sx={{ bgcolor: '#fff', border: '1px solid #e8e8e8', borderRadius: 2, p: 2, my: 0.75 }}>
                            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 1 }}>
                                <Skeleton variant="circular" width={40} height={40} />
                                <Box sx={{ flex: 1 }}>
                                    <Skeleton variant="text" width="55%" height={16} />
                                    <Skeleton variant="text" width="40%" height={13} />
                                </Box>
                            </Box>
                            <Skeleton variant="text" width="70%" height={13} />
                            <Skeleton variant="rounded" width={160} height={28} sx={{ mt: 1 }} />
                        </Box>
                    ))
                ) : queueList.length === 0 ? (
                    <EmptyQueue />
                ) : (
                    queueList.map(entry => (
                        <QueueCard
                            key={entry.id}
                            entry={entry}
                            currentUserId={currentUserId}
                            onClaim={handleClaim}
                            onCancel={setCancelTarget}
                            onAssign={handleOpenAssign}
                            canAssign={supervisor}
                            claiming={claimingId === entry.id}
                        />
                    ))
                )}
            </Box>

            {/* ── Diálogo de cancelación ── */}
            <Dialog
                open={!!cancelTarget}
                onClose={() => !cancelling && setCancelTarget(null)}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CancelOutlinedIcon sx={{ color: '#d32f2f' }} />
                        Cancelar entrada de la cola
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary">
                        ¿Confirmas que deseas cancelar la entrada de{' '}
                        <strong>{cancelTarget?.clientName || 'este contacto'}</strong>?
                        La conversación volverá al bot.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                    <Button
                        onClick={() => setCancelTarget(null)}
                        disabled={cancelling}
                        sx={{ textTransform: 'none' }}
                    >
                        Volver
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleCancelConfirm}
                        disabled={cancelling}
                        startIcon={cancelling
                            ? <CircularProgress size={14} sx={{ color: '#fff' }} />
                            : <CancelOutlinedIcon />
                        }
                        sx={{
                            textTransform: 'none', fontWeight: 600, borderRadius: 2,
                            bgcolor: '#d32f2f', '&:hover': { bgcolor: '#b71c1c' },
                        }}
                    >
                        {cancelling ? 'Cancelando...' : 'Confirmar cancelación'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Diálogo de derivación a consultor ── */}
            <Dialog
                open={!!assignTarget}
                onClose={() => !assigning && setAssignTarget(null)}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AssignmentIndIcon sx={{ color: '#7c3aed' }} />
                        Derivar a un consultor
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Asigna a <strong>{assignTarget?.clientName || 'este contacto'}</strong> a un consultor.
                        El cliente pasará a su cartera.
                    </Typography>
                    <Autocomplete
                        size="small"
                        loading={loadingAgents}
                        options={agents}
                        getOptionLabel={(o) => o?.name || `Agente ${o?.userId}`}
                        isOptionEqualToValue={(o, v) => o.userId === v?.userId}
                        value={agents.find(a => a.userId === selectedAgentId) || null}
                        onChange={(_, o) => setSelectedAgentId(o?.userId ?? null)}
                        renderInput={(params) => (
                            <TextField {...params} label="Consultor" placeholder="Seleccionar..." />
                        )}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                    <Button onClick={() => setAssignTarget(null)} disabled={assigning} sx={{ textTransform: 'none' }}>
                        Volver
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleAssignConfirm}
                        disabled={assigning || !selectedAgentId}
                        startIcon={assigning ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <AssignmentIndIcon />}
                        sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, bgcolor: '#7c3aed', '&:hover': { bgcolor: '#6d28d9' } }}
                    >
                        {assigning ? 'Derivando...' : 'Derivar'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ColaEsperaPage;
